/**
 * @file useScoutReportTranslation.ts
 * @description Hook for translating scout reports via the translateScoutReport
 * GraphQL mutation — on demand from the picker, and automatically into the
 * viewer's language when the caller supplies one (SE-6510). Manages locale
 * selection, loading, failure, and translated text so the ReportItem container
 * can swap between original and translated content without re-fetching.
 * @module reports
 */

import { useCallback, useEffect, useRef, useState } from "react";

import { normalizeTranslatedReportText } from "@/features/reports/utils/normalizeTranslatedReportText";
import type { UiLocale } from "@/constants/locales";
import {
  SupportedLocale,
  useReportsTranslateScoutReportMutation,
} from "@/generated/graphql";

const LOG_PREFIX = "[ScoutReport:Translation]";

/**
 * How long a single translation attempt may stay in flight before it is treated
 * as failed. The Bedrock path behind `translateScoutReport` has no timeout,
 * circuit breaker or rate limit of its own (SE-6510), so without this a stalled
 * provider leaves the report spinning forever with no way back to readable
 * content.
 */
export const TRANSLATION_TIMEOUT_MS = 30_000;

/**
 * Props for the useScoutReportTranslation hook
 */
interface UseScoutReportTranslationProps {
  /** The report ID to translate */
  reportId: string;
  /**
   * The locale to translate into automatically, or `null` for "do not".
   *
   * `null` is the caller's way of saying *any* of: the feature is off, the
   * viewer is not known yet, the viewer has no offered language, or the report
   * is already in the viewer's language. The hook deliberately cannot tell
   * those apart — deciding between them is
   * `resolveScoutReportAutoTranslate`'s job, and duplicating that judgement
   * here is how the two would drift.
   */
  autoTranslateLocale?: UiLocale | null;
}

/**
 * Return value from the useScoutReportTranslation hook
 */
interface UseScoutReportTranslationReturn {
  /** Locale whose translation is on screen right now (null means the original) */
  selectedLocale: string | null;
  /** Set locale without triggering a translation request */
  setLocale: (locale: string) => void;
  /** Trigger translation to the given locale */
  translate: (locale: SupportedLocale) => Promise<void>;
  /** Whether a translation request is in flight */
  isTranslating: boolean;
  /**
   * The locale whose translation failed, or null when nothing has failed.
   *
   * Deliberately NOT the provider's error message. The AC forbids raw error
   * text reaching the screen, and the surest way to honour that is for the
   * message never to leave this hook — it goes to the console, and the view
   * gets a fact ("translation into fr_FR is unavailable") it can phrase itself.
   */
  failedLocale: string | null;
  /** The translated report text, or null if showing original */
  translatedText: string | null;
  /** Reset state back to showing the original report text */
  resetToOriginal: () => void;
  /** Re-attempt the translation that failed. User-initiated; never automatic. */
  retry: () => void;
}

/**
 * Rejects if `promise` has not settled within `timeoutMs`.
 * @param promise - The in-flight work
 * @param timeoutMs - Milliseconds to wait before giving up
 * @returns The promise's value
 */
function withTimeout<T>(promise: Promise<T>, timeoutMs: number): Promise<T> {
  // A ref-shaped holder rather than a reassigned local: the timer id is only
  // knowable inside the executor, and `functional/no-let` rules out a `let`.
  const handle: { current?: ReturnType<typeof setTimeout> } = {};
  const expiry = new Promise<never>((_resolve, reject) => {
    handle.current = setTimeout(() => {
      reject(new Error("Scout report translation timed out"));
    }, timeoutMs);
  });
  return Promise.race([promise, expiry]).finally(() => {
    if (handle.current !== undefined) clearTimeout(handle.current);
  });
}

/**
 * Manages translation of a scout report, on demand and by default.
 *
 * Calls `translateScoutReport` and caches the result per locale in a ref, so
 * toggling back to a locale already fetched in this session costs nothing and
 * cannot double-charge the provider. Three invariants earn their keep:
 *
 * - **Exactly one automatic attempt per (report, locale) run.** A ref, not
 *   state, records the target that was acted on, so re-renders and effect
 *   re-runs cannot re-fire. The record is cleared when the caller withdraws the
 *   target (the modal closing), so reopening a report applies the default
 *   again — from cache, without a second mutation.
 * - **Superseded responses are dropped.** Each attempt takes a monotonic id;
 *   a response whose id is stale writes nothing, so picking French then German
 *   leaves German on screen however the two responses interleave.
 * - **A failure never blanks the report.** Translated text is only ever
 *   replaced on success; on failure or timeout the state falls back to the
 *   original and records which locale failed, which is what the view turns into
 *   a non-blocking notice with a retry.
 *
 * Translated text is passed through normalizeTranslatedReportText first, since
 * the translation pipeline returns escape sequences as literal text (SE-6969).
 * @param props - Hook configuration
 * @param props.reportId - The scout report ID to translate
 * @param props.autoTranslateLocale - Locale to translate into automatically, or null
 * @returns Translation state and action handlers
 */
export function useScoutReportTranslation({
  reportId,
  autoTranslateLocale = null,
}: UseScoutReportTranslationProps): UseScoutReportTranslationReturn {
  const [selectedLocale, setSelectedLocale] = useState<string | null>(null);
  const [translatedText, setTranslatedText] = useState<string | null>(null);
  const [failedLocale, setFailedLocale] = useState<string | null>(null);
  const [pendingLocale, setPendingLocale] = useState<string | null>(null);

  const [translateMutation, { loading: isMutationLoading }] =
    useReportsTranslateScoutReportMutation();

  const cacheRef = useRef<Map<string, string>>(new Map());
  const requestIdRef = useRef(0);
  const lastAutoTargetRef = useRef<string | null>(null);

  const translate = useCallback(
    async (locale: SupportedLocale) => {
      if (!reportId) return;

      const requestId = ++requestIdRef.current;
      setFailedLocale(null);

      const cached = cacheRef.current.get(locale);
      if (cached !== undefined) {
        setTranslatedText(cached);
        setSelectedLocale(locale);
        setPendingLocale(null);
        return;
      }

      setPendingLocale(locale);

      try {
        const result = await withTimeout(
          translateMutation({
            variables: {
              input: {
                scoutReportId: reportId,
                locale,
              },
            },
          }),
          TRANSLATION_TIMEOUT_MS
        );

        if (requestId !== requestIdRef.current) return;

        const translated =
          result.data?.translateScoutReport?.translatedQnaText ?? null;

        if (translated === null) {
          // A non-null SDL return type with a null body is still "no
          // translation to show" — the original stays, and the viewer is told.
          console.warn(LOG_PREFIX, "Translation returned no text", {
            reportId,
            locale,
          });
          setTranslatedText(null);
          setSelectedLocale(null);
          setFailedLocale(locale);
          return;
        }

        const normalized = normalizeTranslatedReportText(translated);
        cacheRef.current.set(locale, normalized);
        setTranslatedText(normalized);
        setSelectedLocale(locale);
      } catch (err) {
        // The provider's message stays here. It is diagnostic, it is not
        // written for a scout, and the AC forbids it reaching the screen.
        console.warn(LOG_PREFIX, "Translation failed", {
          reportId,
          locale,
          error: err instanceof Error ? err.message : err,
        });
        if (requestId !== requestIdRef.current) return;
        setFailedLocale(locale);
        setTranslatedText(null);
        setSelectedLocale(null);
      } finally {
        if (requestId === requestIdRef.current) setPendingLocale(null);
      }
    },
    [reportId, translateMutation]
  );

  const resetToOriginal = useCallback(() => {
    // Supersede anything in flight so a late response cannot re-translate a
    // report the viewer has explicitly asked to see in its original language.
    requestIdRef.current += 1;
    setSelectedLocale(null);
    setTranslatedText(null);
    setFailedLocale(null);
    setPendingLocale(null);
  }, []);

  const setLocale = useCallback((locale: string) => {
    setSelectedLocale(locale);
  }, []);

  const retry = useCallback(() => {
    if (!failedLocale) return;
    void translate(failedLocale as SupportedLocale);
  }, [failedLocale, translate]);

  // The one automatic attempt. `lastAutoTargetRef` is a ref rather than state
  // precisely so writing it cannot itself schedule a render and re-enter.
  useEffect(() => {
    if (!reportId || !autoTranslateLocale) {
      lastAutoTargetRef.current = null;
      return;
    }
    const target = `${reportId}::${autoTranslateLocale}`;
    if (lastAutoTargetRef.current === target) return;
    lastAutoTargetRef.current = target;
    void translate(autoTranslateLocale as SupportedLocale);
  }, [reportId, autoTranslateLocale, translate]);

  return {
    selectedLocale,
    setLocale,
    translate,
    isTranslating: isMutationLoading || pendingLocale !== null,
    failedLocale,
    translatedText,
    resetToOriginal,
    retry,
  };
}
